/**
 * Trade History Panel
 *
 * Displays filled/closed orders with filtering, pagination, summary stats,
 * running P&L, and CSV export.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  History,
  RefreshCw,
  Loader2,
  AlertCircle,
  X,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Target,
  DollarSign,
  Filter,
} from 'lucide-react';
import { brokerAPI, type BrokerOrder } from '../../api/broker';

// ────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const DATE_RANGES = [
  { label: 'Last 7d', days: 7 },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'All', days: 0 },
] as const;

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

const formatCurrency = (val: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);

const formatPrice = (val: string | null): string => {
  if (!val) return '-';
  const n = parseFloat(val);
  return isNaN(n) ? '-' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
};

const formatQty = (val: string | null): string => {
  if (!val) return '-';
  const n = parseFloat(val);
  return isNaN(n) ? '-' : n.toLocaleString('en-US', { maximumFractionDigits: 4 });
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

const computeTotalValue = (order: BrokerOrder): number => {
  const qty = parseFloat(order.filled_qty || order.qty || '0');
  const price = parseFloat(order.filled_avg_price || '0');
  return qty * price;
};

const daysAgo = (days: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
};

// ────────────────────────────────────────────────────────────
// Skeleton rows
// ────────────────────────────────────────────────────────────

function SkeletonRows({ count = 8 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={i} className="border-b border-gray-700/50">
          {Array.from({ length: 8 }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 bg-gray-700 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Summary Stats Card
// ────────────────────────────────────────────────────────────

interface SummaryStatsProps {
  totalTrades: number;
  winRate: number;
  avgProfit: number;
  totalPL: number;
}

function SummaryStats({ totalTrades, winRate, avgProfit, totalPL }: SummaryStatsProps) {
  const stats = [
    {
      label: 'Total Trades',
      value: totalTrades.toLocaleString(),
      icon: BarChart3,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
    },
    {
      label: 'Win Rate',
      value: `${winRate.toFixed(1)}%`,
      icon: Target,
      color: winRate >= 50 ? 'text-green-400' : 'text-red-400',
      bg: winRate >= 50 ? 'bg-green-500/10' : 'bg-red-500/10',
    },
    {
      label: 'Avg P/L',
      value: formatCurrency(avgProfit),
      icon: avgProfit >= 0 ? TrendingUp : TrendingDown,
      color: avgProfit >= 0 ? 'text-green-400' : 'text-red-400',
      bg: avgProfit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
    },
    {
      label: 'Total P&L',
      value: formatCurrency(totalPL),
      icon: DollarSign,
      color: totalPL >= 0 ? 'text-green-400' : 'text-red-400',
      bg: totalPL >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-5 py-4 border-b border-gray-700/50">
      {stats.map((s) => (
        <div key={s.label} className="flex items-center gap-3 bg-gray-800/60 rounded-lg px-4 py-3">
          <div className={`p-2 rounded-lg ${s.bg}`}>
            <s.icon className={`w-4 h-4 ${s.color}`} />
          </div>
          <div>
            <p className="text-gray-400 text-xs">{s.label}</p>
            <p className={`text-sm font-semibold ${s.color}`}>{s.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

export default function TradeHistory() {
  const [allOrders, setAllOrders] = useState<BrokerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [dateRange, setDateRange] = useState<number>(30);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [sideFilter, setSideFilter] = useState<'all' | 'buy' | 'sell'>('all');
  const [page, setPage] = useState(1);

  // ── Fetch orders ──────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brokerAPI.getOrders('closed', 500);
      setAllOrders(data.orders);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch trade history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, symbolSearch, sideFilter]);

  // ── Filtered + paginated orders ───────────────────────────

  const filteredOrders = useMemo(() => {
    let result = allOrders;

    // Date range filter
    if (dateRange > 0) {
      const cutoff = daysAgo(dateRange);
      result = result.filter((o) => {
        const d = o.filled_at || o.updated_at || o.created_at;
        return d ? new Date(d) >= cutoff : false;
      });
    }

    // Symbol search
    if (symbolSearch.trim()) {
      const term = symbolSearch.trim().toUpperCase();
      result = result.filter((o) => o.symbol.toUpperCase().includes(term));
    }

    // Side filter
    if (sideFilter !== 'all') {
      result = result.filter((o) => o.side?.toLowerCase() === sideFilter);
    }

    return result;
  }, [allOrders, dateRange, symbolSearch, sideFilter]);

  // ── Summary stats ─────────────────────────────────────────

  const summaryStats = useMemo((): SummaryStatsProps => {
    if (filteredOrders.length === 0) {
      return { totalTrades: 0, winRate: 0, avgProfit: 0, totalPL: 0 };
    }

    // Build a map of symbol -> trades to pair buys/sells for P&L estimation.
    // For a simple estimate: each filled sell is profit, each filled buy is cost.
    // Net P&L per symbol = sum(sell_value) - sum(buy_value).
    const symbolMap = new Map<string, { buyValue: number; sellValue: number }>();

    for (const order of filteredOrders) {
      const val = computeTotalValue(order);
      if (val === 0) continue;
      const entry = symbolMap.get(order.symbol) || { buyValue: 0, sellValue: 0 };
      if (order.side?.toLowerCase() === 'buy') {
        entry.buyValue += val;
      } else {
        entry.sellValue += val;
      }
      symbolMap.set(order.symbol, entry);
    }

    let totalPL = 0;
    let wins = 0;
    let counted = 0;

    symbolMap.forEach(({ buyValue, sellValue }) => {
      // Only count symbols where both sides exist
      if (buyValue > 0 && sellValue > 0) {
        const pl = sellValue - buyValue;
        totalPL += pl;
        if (pl > 0) wins++;
        counted++;
      }
    });

    const winRate = counted > 0 ? (wins / counted) * 100 : 0;
    const avgProfit = counted > 0 ? totalPL / counted : 0;

    return {
      totalTrades: filteredOrders.length,
      winRate,
      avgProfit,
      totalPL,
    };
  }, [filteredOrders]);

  // ── Pagination ────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const paginatedOrders = useMemo(
    () => filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredOrders, page]
  );

  // ── CSV export ────────────────────────────────────────────

  const handleExport = () => {
    const headers = ['Date', 'Symbol', 'Side', 'Type', 'Qty', 'Avg Fill Price', 'Total Value', 'Status'];
    const rows = filteredOrders.map((o) => [
      o.filled_at || o.updated_at || o.created_at || '',
      o.symbol,
      o.side || '',
      o.type || '',
      o.filled_qty || o.qty || '',
      o.filled_avg_price || '',
      computeTotalValue(o).toFixed(2),
      o.status || '',
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trade_history_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-700/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/10 rounded-lg">
            <History className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Trade History</h3>
            <p className="text-gray-400 text-xs">
              {filteredOrders.length} trade{filteredOrders.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={filteredOrders.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm transition-colors disabled:opacity-40"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={fetchOrders}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {!loading && <SummaryStats {...summaryStats} />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-700/50 bg-gray-800/30">
        <Filter className="w-4 h-4 text-gray-500 shrink-0" />

        {/* Date range */}
        <div className="flex items-center bg-gray-800 rounded-lg overflow-hidden border border-gray-700/50">
          {DATE_RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDateRange(r.days)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                dateRange === r.days
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Symbol search */}
        <div className="relative flex-1 min-w-[140px] max-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            placeholder="Search symbol..."
            value={symbolSearch}
            onChange={(e) => setSymbolSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-800 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
          />
        </div>

        {/* Side filter */}
        <div className="flex items-center bg-gray-800 rounded-lg overflow-hidden border border-gray-700/50">
          {(['all', 'buy', 'sell'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setSideFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                sideFilter === s
                  ? s === 'buy'
                    ? 'bg-green-500/20 text-green-400'
                    : s === 'sell'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-blue-500/20 text-blue-400'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-5 py-3 bg-red-500/10 border-b border-red-500/20">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-red-300 text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700/50 text-gray-400 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Symbol</th>
              <th className="text-left px-4 py-3 font-medium">Side</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Qty</th>
              <th className="text-right px-4 py-3 font-medium">Avg Fill Price</th>
              <th className="text-right px-4 py-3 font-medium">Total Value</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <History className="w-10 h-10 text-gray-600" />
                    <p className="text-gray-400 font-medium">No trades found</p>
                    <p className="text-gray-500 text-xs">
                      Adjust your filters or execute some trades
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order) => {
                const isBuy = order.side?.toLowerCase() === 'buy';
                const totalValue = computeTotalValue(order);
                const statusColor =
                  order.status === 'filled'
                    ? 'bg-green-500/10 text-green-400'
                    : order.status === 'canceled' || order.status === 'cancelled'
                      ? 'bg-red-500/10 text-red-400'
                      : order.status === 'expired'
                        ? 'bg-yellow-500/10 text-yellow-400'
                        : 'bg-gray-500/10 text-gray-400';

                return (
                  <tr
                    key={order.id}
                    className="border-b border-gray-700/30 hover:bg-gray-800/50 transition-colors"
                  >
                    {/* Date */}
                    <td className="px-4 py-3 text-gray-300 text-xs whitespace-nowrap">
                      {formatDate(order.filled_at || order.updated_at || order.created_at)}
                    </td>

                    {/* Symbol */}
                    <td className="px-4 py-3 text-white font-medium">
                      {order.symbol}
                    </td>

                    {/* Side */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
                          isBuy
                            ? 'bg-green-500/15 text-green-400'
                            : 'bg-red-500/15 text-red-400'
                        }`}
                      >
                        {order.side?.toUpperCase() || '-'}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3 text-gray-300 capitalize">
                      {order.type?.replace('_', ' ') || '-'}
                    </td>

                    {/* Qty */}
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      {formatQty(order.filled_qty || order.qty)}
                    </td>

                    {/* Avg Fill Price */}
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      {formatPrice(order.filled_avg_price)}
                    </td>

                    {/* Total Value */}
                    <td className="px-4 py-3 text-right font-mono font-medium">
                      <span className={isBuy ? 'text-red-300' : 'text-green-300'}>
                        {totalValue > 0 ? formatCurrency(totalValue) : '-'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${statusColor}`}
                      >
                        {order.status?.replace('_', ' ') || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700/50">
          <p className="text-gray-400 text-xs">
            Showing {(page - 1) * PAGE_SIZE + 1}
            &ndash;
            {Math.min(page * PAGE_SIZE, filteredOrders.length)} of{' '}
            {filteredOrders.length}
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                    page === pageNum
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-gray-800 hover:bg-gray-700 text-gray-400'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
